/* eslint-disable no-void */

import * as vp from 'vopay';

import { useEffect, useState } from 'react';
import axios from 'axios';

import { useDataFetchIndicator } from '..';

import vopayApi from '../../services/api.config';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';

type HookProps = {
  api: vp.Api;
};

type ReturnTuple = [
  vp.ClientAccountBalanceResponse,
  (requestParams: vp.ClientAccountBalanceRequest) => void,
  () => void,
];

function useFetchClientAccountBalance({ api }: HookProps): ReturnTuple {
  const source = axios.CancelToken.source();
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [params, setParams] = useState<vp.ClientAccountBalanceRequest>();
  const [response, setResponse] = useState<vp.ClientAccountBalanceResponse>({
    ErrorMessage: '',
    Success: false,
    AccountBalance: '',
    AvailableFunds: '',
    Currency: '',
    PendingFunds: '',
    Reserve: '',
  });
  const [shouldSendRequest, setSendRequest] = useState(false);
  const { ClientAccountsEnabled } = useLockedFeatures();

  useEffect(() => {
    if (!ClientAccountsEnabled) {
      return;
    }
    const sendRequest = async (requestParams: vp.ClientAccountBalanceRequest) => {
      setDataFetchingInfo(true, 'loading some data', 'fullScreen');
      const res = await api.fetchClientAccountBalance(requestParams, source);

      setResponse(res);
      setSendRequest(false);
      setDataFetchingInfo(false);

      return () => {};
    };

    if (params && shouldSendRequest) void sendRequest(params);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (requestParams: vp.ClientAccountBalanceRequest) => {
      setParams(requestParams);
      setSendRequest(true);
    },
    () => {
      setSendRequest(false);
      setParams(undefined);
      setResponse({
        ErrorMessage: '',
        Success: false,
        AccountBalance: '',
        AvailableFunds: '',
        Currency: '',
        PendingFunds: '',
        Reserve: '',
      });
    },
  ];
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(useFetchClientAccountBalance);
